import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import cogoToast from 'cogo-toast';
import { saveCircle } from '../../api/circles';
import Modal from '../General/Modal';
import ImageField from './ImageField';
import CircleCreators from './CircleCreators';
import { getRootSMSUrl } from '../../helpers/helpers';
import CircleVisibility from './CircleVisibility';
import CircleCategory, { CATEGORY_HINT } from './CircleCategory';
import { message, confirmDiscard } from './circle_helpers';

function CircleEditor({
  circle,
  onSaved,
  onClose,
  onDirtyChange,
  placementOptions,
  placementsReady,
  placementError,
  onRetryPlacements,
  industries,
  categoriesReady,
  categoryError,
  onRetryCategories
}) {
  const [draft, setDraft] = useState(circle);
  const [savedCircle, setSavedCircle] = useState(circle);
  const [saveAction, setSaveAction] = useState(null);
  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [editorView, setEditorView] = useState(circle.id ? 'creators' : 'details');
  const [error, setError] = useState('');
  const [placementChanges, setPlacementChanges] = useState({});
  const selectedPlacements = placementOptions.filter(option => placementChanges[option.key] ?? option.selected).map(option => option.key);
  const placementDirty = placementOptions.some(option => selectedPlacements.includes(option.key) !== !!option.selected);
  const contentDirty = JSON.stringify(draft) !== JSON.stringify(savedCircle);
  const unpublished = placementOptions.some(option => selectedPlacements.includes(option.key) !== !!option.published);
  const publishFull = placementOptions.filter(option => option.publishFull && selectedPlacements.includes(option.key));
  const dirty = contentDirty || placementDirty;
  useEffect(() => {
    onDirtyChange(dirty);
  }, [dirty, onDirtyChange]);
  useEffect(() => {
    const refreshPublishedState = old => ({ ...old, updatedAt: circle.updatedAt, isSearchable: circle.isSearchable });
    setDraft(refreshPublishedState);
    setSavedCircle(refreshPublishedState);
  }, [circle.updatedAt, circle.isSearchable]);
  const update = (key, value) => setDraft(old => ({ ...old, [key]: value }));
  useEffect(() => {
    const warn = event => {
      if (dirty) {
        event.preventDefault();
        event.returnValue = '';
      }
    };
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [dirty]);
  const close = () => {
    if (busy || uploading) return;
    if (dirty) confirmDiscard('Discard unsaved Circle edits?', onClose);
    else onClose();
  };
  const save = async action => {
    const makingPublic = !savedCircle.isSearchable && !!draft.isSearchable;
    setSaveAction(action);
    setBusy(true);
    setError('');
    try {
      const response =
        savedCircle.id && !contentDirty && (action !== 'publish' || savedCircle.isSearchable)
          ? { circle: savedCircle }
          : await saveCircle({
              id: draft.id,
              updatedAt: draft.updatedAt,
              title: draft.title,
              description: draft.description,
              coverImage: draft.coverImage,
              ...((action === 'publish' || !!draft.isSearchable !== !!savedCircle.isSearchable) && {
                isPublic: action === 'publish' || !!draft.isSearchable
              }),
              ...((draft.Industry_id !== savedCircle.Industry_id ||
                draft.Department_id !== savedCircle.Department_id ||
                draft.Category_id !== savedCircle.Category_id) && {
                Industry_id: draft.Industry_id ?? null,
                Department_id: draft.Department_id ?? null,
                Category_id: null
              }),
              members: draft.members.map(member => ({ User_id: member.User_id, description: member.description }))
            });
      setSavedCircle(response.circle);
      setDraft(response.circle);
      await onSaved(response.circle, selectedPlacements, action);
      setBusy(false);
      onClose();
      cogoToast.success(
        action === 'publish'
          ? 'Circle published. Selected placements are live.'
          : makingPublic
          ? 'Circle is now public. Review and publish any placement changes when ready.'
          : 'Circle saved. Review and publish any placement changes when ready.'
      );
    } catch (error) {
      setError(message(error));
      setBusy(false);
    }
  };
  return (
    <Modal close={close} container_class='circle-editor circle-curation-editor' focusTrap labelledBy='circle-editor-title'>
      <header className='circle-toolbar'>
        <div>
          <small>{circle.id ? 'Edit Circle' : 'Create Circle'}</small>
          <h2 id='circle-editor-title'>{draft.title || 'New Circle'}</h2>
          <p>
            {draft.members.length} creators · {draft.isSearchable ? 'Public' : 'Hidden'}
            {dirty ? ' · Unsaved changes' : ''}
          </p>
        </div>
        <div className='circle-actions'>
          {circle.id && savedCircle.isSearchable && (
            <a className='circle-shop-link' href={`${getRootSMSUrl()}/shop/circles/${circle.id}`} target='_blank' rel='noopener noreferrer'>
              View Circle ↗
            </a>
          )}
        </div>
        <button disabled={busy || uploading} onClick={close}>
          Close
        </button>
      </header>
      <div className='circle-curation-context'>
        <span>Featured in</span>
        {placementOptions
          .filter(option => option.published)
          .map(option => (
            <span className='circle-placement-badge' key={option.key}>
              {option.label}
            </span>
          ))}
        {!placementOptions.some(option => option.published) && <span>No published placements</span>}
        <small>Creator and detail changes apply everywhere this Circle appears.</small>
      </div>
      <nav className='circle-toolbar circle-editor-tabs' aria-label='Circle editor views'>
        <button aria-pressed={editorView === 'creators'} onClick={() => setEditorView('creators')}>
          Creators ({draft.members.length})
        </button>
        <button aria-pressed={editorView === 'details'} onClick={() => setEditorView('details')}>
          Details & placements
        </button>
      </nav>
      <fieldset disabled={busy || editorView !== 'creators'} hidden={editorView !== 'creators'}>
        <CircleCreators members={draft.members} onChange={members => update('members', members)} />
      </fieldset>
      <fieldset disabled={busy || editorView !== 'details'} hidden={editorView !== 'details'}>
        <div className='circle-editor-visibility'>
          <CircleVisibility
            title={draft.title}
            checked={!!draft.isSearchable}
            disabled={busy}
            onChange={value => update('isSearchable', value === !!savedCircle.isSearchable ? savedCircle.isSearchable : value)}
          />
          <small>Applies when you save.</small>
        </div>
        {!draft.isSearchable && savedCircle.isSearchable && placementOptions.some(option => option.published) && (
          <p role='alert'>
            Remove this Circle from its published placements before hiding it:{' '}
            {placementOptions
              .filter(option => option.published)
              .map(option => option.label)
              .join(', ')}
            .
          </p>
        )}
        <label>
          Circle name
          <input value={draft.title} maxLength={255} onChange={event => update('title', event.target.value)} />
        </label>
        <label>
          Circle description
          <textarea
            value={draft.description || ''}
            placeholder='Add a description for this Circle'
            aria-describedby='circle-description-hint'
            maxLength={10000}
            onChange={event => update('description', event.target.value)}
          />
        </label>
        <small id='circle-description-hint'>Shown below the Circle title during onboarding.</small>
        <label>
          Primary category
          <CircleCategory
            circle={draft}
            industries={industries}
            disabled={busy || !categoriesReady}
            onChange={category => setDraft(old => ({ ...old, ...category }))}
          />
        </label>
        <small id='circle-category-hint'>{CATEGORY_HINT}</small>
        {!categoriesReady && !categoryError && <p>Loading categories…</p>}
        {categoryError && (
          <p role='alert'>
            {categoryError} <button onClick={onRetryCategories}>Retry categories</button>
          </p>
        )}
        <ImageField
          label='Circle cover image'
          value={draft.coverImage}
          onChange={value => {
            update('coverImage', value);
            setUploading(false);
          }}
          onUploading={setUploading}
        />
      </fieldset>
      <fieldset className='circle-builder-placements' disabled={busy || !placementsReady} hidden={editorView !== 'details'}>
        <legend>Feature Placement</legend>
        {placementOptions.map(option => {
          const checked = selectedPlacements.includes(option.key);
          return (
            <label className='circle-check' key={option.key}>
              <input
                type='checkbox'
                checked={checked}
                disabled={option.full && !option.selected && !checked}
                onChange={event => {
                  const checked = event.target.checked;
                  setPlacementChanges(old => ({ ...old, [option.key]: checked }));
                }}
              />
              Feature in {option.label}
              {option.full && !option.selected && !checked && <small>Placement is full</small>}
            </label>
          );
        })}
      </fieldset>
      {!placementsReady && !placementError && (
        <p role='status'>{editorView === 'details' ? 'Loading placements…' : 'Loading placements before you can save this Circle…'}</p>
      )}
      {placementError && (
        <p role='alert'>
          {editorView === 'details' ? placementError : 'Placements could not be loaded. Retry to enable saving.'}{' '}
          <button onClick={onRetryPlacements}>Retry placements</button>
        </p>
      )}
      {!!publishFull.length && <p>Review and publish pending removals to make room in {publishFull.map(option => option.label).join(' and ')}.</p>}
      {error && <p role='alert'>{error}</p>}
      <footer className='circle-toolbar'>
        <button disabled={busy || uploading} onClick={close}>
          Cancel
        </button>
        <button
          disabled={busy || uploading || !placementsReady || !draft.title.trim() || (savedCircle.id && !dirty && !unpublished)}
          onClick={() => save('review')}
        >
          {busy && saveAction === 'review' ? 'Saving…' : 'Save'}
        </button>
        <button
          className='primary'
          disabled={
            busy ||
            uploading ||
            !placementsReady ||
            !!publishFull.length ||
            (savedCircle.isSearchable && !draft.isSearchable) ||
            !draft.title.trim() ||
            (savedCircle.id && savedCircle.isSearchable && !dirty && !unpublished)
          }
          onClick={() => save('publish')}
        >
          {busy && saveAction === 'publish' ? 'Publishing…' : 'Save and Publish'}
        </button>
      </footer>
    </Modal>
  );
}

CircleEditor.propTypes = {
  circle: PropTypes.object.isRequired,
  onSaved: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
  onDirtyChange: PropTypes.func.isRequired,
  placementOptions: PropTypes.arrayOf(
    PropTypes.shape({
      key: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
      selected: PropTypes.bool,
      published: PropTypes.bool,
      full: PropTypes.bool,
      publishFull: PropTypes.bool
    })
  ).isRequired,
  placementsReady: PropTypes.bool.isRequired,
  placementError: PropTypes.string,
  industries: PropTypes.array.isRequired,
  categoriesReady: PropTypes.bool.isRequired,
  categoryError: PropTypes.string,
  onRetryCategories: PropTypes.func.isRequired,
  onRetryPlacements: PropTypes.func.isRequired
};

export default CircleEditor;
